// src/components/credits/CreditBalanceCard.jsx
//
// Renders a CreditBalanceResponse. Four states, branched in EXACTLY this order:
//   1. expired: true    -> the trial window elapsed. "Trial ended", with the numbers
//                          FROZEN on that window (never a fresh-period zero). Expired
//                          OUTRANKS exhaustion — a tenant whose window elapsed never
//                          reports CREDITS_EXHAUSTED.
//   2. unlimited: true  -> plan name + consumed, no gauge, "Illimitato" badge.
//   3. has_plan: false  -> BLOCKED. Enforcement is fail-closed, so this comes back as
//                          can_generate: false / block_reason "NO_PLAN". Consumed-only
//                          numbers (the allowance and the spendable total are null but
//                          consumed is real): never a crash, never "0 remaining". A
//                          tenant with customer_id: null lands here too. ⭐ The
//                          PURCHASED pot still renders here — those credits survive a
//                          cancellation and return on resubscribe.
//   4. otherwise        -> the wallet block (below).
//
// ⛔ 2026-09-23: `noGauge` and `subscriptionEnded` were DELETED (see balanceState()),
// together with the workspace-bundle badge, the "N crediti scadono il…" line and the
// `overAllowance` heuristic — all fed by fields the backend withdrew.
//
// The wallet block (gauge / expired states) shows TWO POTS, both verbatim off the wire:
//   - `remaining_credits`   the SPENDABLE total (= allowance_remaining +
//                           purchased_remaining), as the headline. ⛔ No denominator:
//                           the purchased pot has no ceiling, so "1.500 / 5.000" would
//                           put two different axes on one line;
//   - `allowance_remaining` of `allowance_credits` — the period's grant, lost at the
//                           next grant, with a neutral "periodo fino al …" bound
//                           (`period_end` is a PREDICTION on a paid plan — never
//                           "si rinnova");
//   - `purchased_remaining` never expires. Hidden at exactly 0 (ambiguous), rendered
//                           as a DEFICIT when negative (never clamped away).
// The gauge divides CONSUMED by the ALLOWANCE pot alone — the only pot with a ceiling.
//
// The lock is NEVER derived from the numbers. `remaining_credits` is null for two
// OPPOSITE reasons — an unlimited plan (may run forever) and no plan at all (blocked) —
// so only `can_generate` / `block_reason` separate them.
//
// Audience: `canAssignPlan` selects the blocked-state copy. A member is told to contact
// their administrator (they cannot fix it); the superadmin tenant detail — which carries
// the subscription-assignment control on the very same page — is pointed at that control
// instead, because there the reader IS the administrator.
//
// `showMoney` reveals cost_usd (superadmin only — null for everyone else). The pot
// lines carry NO money.
//
// No trend chart here, by contract: /usage?group_by=day returns 422, there is no
// time-series in v1.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';
import {
  formatCredits, formatMoney, consumedFraction, formatPeriodLabel,
  formatRenewalDate, parseDecimal,
} from '../../utils/creditsFormat.js';
import { describeRefusal, refusalFromBlockReason } from '../../utils/runRefusal.js';

const FONT = FONT_SANS;
const COLOR = { text: COLORS.ink, muted: COLORS.textSecondary, accent: COLORS.accent, okFg: SEMANTIC.verde.fg, border: COLORS.border };
const cardStyle = {
  background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLOR.border}`,
  padding: '20px 22px', fontFamily: FONT, color: COLOR.text,
};
const chipStyle = {
  display: 'inline-block', padding: '3px 10px', borderRadius: RADII.pill,
  fontSize: 12, fontWeight: 600,
};

function Gauge({ fraction }) {
  const { t } = useTranslation('credits');
  const pct = Math.round((fraction || 0) * 100);
  const barColor = fraction >= 0.9 ? SEMANTIC.errore.fg : fraction >= 0.7 ? SEMANTIC.attenzione.fg : COLOR.okFg;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ height: 10, borderRadius: 6, background: COLORS.border, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: barColor, transition: 'width 0.3s' }} />
      </div>
      <div style={{ fontSize: 11, color: COLOR.muted, marginTop: 5 }}>{t('balance.gauge', { pct })}</div>
    </div>
  );
}

/** Blocked banner — the state is told by can_generate/block_reason, never by the numbers. */
function BlockedNotice({ title, body }) {
  return (
    <div style={{
      marginTop: 14, padding: '10px 12px', borderRadius: RADII.controlInner,
      background: SEMANTIC.errore.bg, color: SEMANTIC.errore.fg, fontSize: 12,
    }}>
      <strong style={{ display: 'block', fontSize: 13, marginBottom: 2 }}>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

/**
 * The purchased pot. ⚠️ A NEGATIVE value is a standing deficit, not a bug: an
 * overspend clamps `allowance_remaining` at 0 and spills the rest here. It is
 * rendered as owed rather than clamped to zero, because a customer who owes
 * credits needs to know before their next top-up appears to vanish.
 */
function PurchasedLine({ value, t }) {
  const deficit = value < 0;
  return (
    <div style={{ fontSize: 12, color: deficit ? SEMANTIC.attenzione.fg : COLOR.muted, marginTop: 6 }}>
      {deficit
        ? t('balance.pots.purchasedDeficit', { credits: formatCredits(Math.abs(value)) })
        : t('balance.pots.purchased', { credits: formatCredits(value) })}
    </div>
  );
}

/**
 * Which of the FOUR states to render. The order is the contract's, not ours:
 * `expired` outranks everything (an elapsed window is terminal and blocks even
 * with credits left), `unlimited` is caught before the gauge because it has no
 * allowance to divide by, and `blocked` (no plan) is what fail-closed
 * enforcement reports.
 *
 * ⛔ 2026-09-23 — TWO branches were DELETED, do not reinstate:
 *   `subscriptionEnded` — the backend withdrew the subscription-ended flag.
 *   `noGauge` (`has_plan && allowance == null && !unlimited`) — UNREACHABLE:
 *      `allowance_credits` is null only for no-plan or unlimited, and the one
 *      NULL-allowance plan (`special`) seeds `enforces_limit=False`, so the
 *      `unlimited` branch catches it first. An unexpected null now falls through
 *      to the `gauge` state, which without an allowance renders consumed-only
 *      numbers and no gauge — visibly incomplete rather than silently explained
 *      away by a note that cannot be true.
 */
function balanceState({ has_plan, unlimited, expired }) {
  if (expired) return 'expired';
  if (unlimited) return 'unlimited';
  if (!has_plan) return 'blocked';
  return 'gauge';
}

export default function CreditBalanceCard({ balance, showMoney = false, loading = false, canAssignPlan = false }) {
  const { t } = useTranslation('credits');
  if (loading) return <div style={{ ...cardStyle, color: COLOR.muted, fontSize: 13 }}>{t('balance.loading')}</div>;
  if (!balance) return null;

  const {
    plan_name, allowance_credits, allowance_remaining, purchased_remaining,
    consumed_credits, remaining_credits, cost_usd, period_start, period_end,
  } = balance;

  const state = balanceState(balance);
  // Absent period_end (old backend) still yields the calendar-month label.
  const periodLabel = formatPeriodLabel(period_start, period_end);
  const money = showMoney ? formatMoney(cost_usd) : null;
  // ⛔ The gauge divides by the ALLOWANCE pot — the only pot with a ceiling.
  const fraction = consumedFraction(consumed_credits, allowance_credits);

  // The spendable headline + the allowance breakdown only make sense with a real
  // allowance: an expired trial keeps its (frozen) one, the other states have none.
  const showRemaining = (state === 'gauge' || state === 'expired') && allowance_credits != null;

  // ⚠️ `period_end` is DISPLAY ONLY and, on a paid plan, a PREDICTION: the period
  // really ends when the NEXT GRANT is written, so an unpaid renewal keeps the
  // wallet empty past this date. And `renews` is NOT on this payload, so we cannot
  // tell a rolling period from a live trial. ⇒ neutral wording that is true for
  // both ("periodo fino al …"), dropped entirely when the bound is null.
  const periodEndsOn = formatRenewalDate(period_end);

  // ⛔ HIDE at exactly 0, render otherwise — including NEGATIVE, which is a
  // standing deficit and never clamped away. The wire carries no "was ever
  // bought" flag, so 0 is ambiguous between "never bought" and "spent to zero";
  // hiding it is the honest cut.
  const purchasedNum = parseDecimal(purchased_remaining);
  const showPurchased = purchasedNum != null && purchasedNum !== 0;

  // Blocked by a state that draws NO notice of its own. `expired` and `blocked`
  // say their own piece below; this covers the rest — CREDITS_EXHAUSTED,
  // NO_ALLOWANCE, and whatever ships next — from `block_reason` alone, sharing
  // the run-refusal copy so the card and the Generate button never describe one
  // lock in two different ways.
  const lockNotice =
    balance.can_generate === false && state !== 'expired' && state !== 'blocked'
      ? describeRefusal(refusalFromBlockReason(balance.block_reason || 'BLOCKED'), { periodEnd: period_end })
      : null;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('balance.title')}</h3>
        {periodLabel && <span style={{ fontSize: 12, color: COLOR.muted }}>{periodLabel}</span>}
      </div>

      {(plan_name || state === 'unlimited') && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {plan_name && (
            <span style={{ ...chipStyle, background: COLORS.accentSoft, color: COLOR.accent }}>{plan_name}</span>
          )}
          {state === 'unlimited' && (
            <span style={{ ...chipStyle, background: SEMANTIC.verde.bg, color: SEMANTIC.verde.fg }}>
              {t('balance.unlimitedBadge')}
            </span>
          )}
        </div>
      )}

      {showRemaining ? (
        <>
          {/* The SPENDABLE total, verbatim — negative included. ⛔ No denominator:
              this is allowance + purchased, and the purchased pot has no ceiling,
              so "1.500 / 5.000" would put two different axes on one line. */}
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 30, fontWeight: 700 }}>{formatCredits(remaining_credits)}</span>
            <span style={{ fontSize: 14, color: COLOR.muted }}>{t('balance.spendable')}</span>
          </div>

          {/* Pot 1 — the period's grant. Lost at the next grant. */}
          <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 10 }}>
            {t('balance.pots.allowance', {
              remaining: formatCredits(allowance_remaining),
              allowance: formatCredits(allowance_credits),
            })}
            {periodEndsOn && ` — ${t('balance.pots.periodEnds', { date: periodEndsOn })}`}
          </div>

          {/* Pot 2 — never expires. Hidden at exactly 0. */}
          {showPurchased && <PurchasedLine value={purchasedNum} t={t} />}

          {/* Consumed over the allowance, clamped — truthful whatever the wallet holds. */}
          {fraction != null && <Gauge fraction={fraction} />}

          <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 10 }}>
            {t('balance.consumedInPeriod')} <strong style={{ color: COLOR.text }}>{formatCredits(consumed_credits)}</strong>
          </div>
        </>
      ) : (
        <>
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 30, fontWeight: 700 }}>{formatCredits(consumed_credits)}</span>
            <span style={{ fontSize: 14, color: COLOR.muted }}>{t('balance.consumed')}</span>
          </div>
          {/* ⭐ KEEP the purchased pot even with NO plan: those credits survive a
              cancellation and return on resubscribe, so blanking them would imply
              the customer's money vanished. */}
          {showPurchased && <PurchasedLine value={purchasedNum} t={t} />}
        </>
      )}

      {state === 'unlimited' && (
        <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 8 }}>{t('balance.unlimitedNote')}</div>
      )}

      {state === 'expired' && (
        <BlockedNotice title={t('balance.expired.title')} body={t('balance.expired.body')} />
      )}
      {state === 'blocked' && (
        <BlockedNotice
          title={t('balance.blockedNoPlan.title')}
          body={canAssignPlan ? t('balance.blockedNoPlan.admin') : t('balance.blockedNoPlan.member')}
        />
      )}
      {/* PB4: a tenant can be blocked while its gauge still reads normally —
          CREDITS_EXHAUSTED, NO_ALLOWANCE (a plan that is fine but whose credit
          period was never opened), or a reason we do not know yet. The two
          branches above cover TRIAL_EXPIRED and NO_PLAN by state; this one covers
          the rest, from `block_reason` alone. ⚠️ Never from the numbers: a gauge
          at zero is not the lock, and a full gauge is not proof of one — the
          wallet is the customer's and another workspace may have drained it. */}
      {lockNotice && <BlockedNotice title={lockNotice.title} body={lockNotice.body} />}

      {money && (
        <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 8 }}>
          {t('balance.estimatedCost')} <strong style={{ color: COLOR.text }}>{money}</strong>
        </div>
      )}
    </div>
  );
}
